import { useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";

import { IVehicle } from "../../models/Vehicle";
import openInNewTab from "../../utils/openInNewTab";
import Card from "../shared/Card/Card";
import { IPart, PartStatus } from "../../models/Part";
import useVehicleSingle from "../../data/useVehicleSingle";
import useJobsByVehicle from "../../data/useJobsByVehicle";
import PartImage from "../stock/PartImage";
import Tabs from "../shared/Tabs/Tabs";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import PriceFormat from "../../utils/priceFormat";
import { useLabelGenerator } from "../../hooks/useLabelGenerator";
import VehiclePartHistory from "./VehiclePartHistory";
import RsCoreImage from "../core/RsCoreImage";
import Tooltip from "../shared/Tooltip";

const PAGE_SIZE = 10;

const partStatusColour = (status: PartStatus) => {
  switch (status) {
    case PartStatus["On The Car"]:
      return "bg-orange-100 text-orange-700";
    case PartStatus.Damaged:
      return "bg-red-100 text-red-700";
    case PartStatus.Missing:
    case PartStatus["Not Accessible"]:
      return "bg-yellow-100 text-yellow-700";
    case PartStatus.QC:
      return "bg-purple-100 text-purple-700";
    case PartStatus.Dismantled:
      return "bg-green-100 text-green-700";
    case PartStatus.Sold:
      return "bg-blue-100 text-blue-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

/** Small square icon button used in the actions column. */
const IconButton = ({
  label,
  onClick,
  disabled = false,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) => (
  <Tooltip content={label}>
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="flex rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:text-gray-200 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  </Tooltip>
);

export default function VehiclePartsCard({ vehicle: passedVehicle, vehicleId }: { vehicle?: IVehicle, vehicleId?: string }) {
  const { t } = useTranslation("vehicles");
  const { vehicle: singleVehicle } = useVehicleSingle(vehicleId);
  const { jobs, createWithPart } = useJobsByVehicle(vehicleId || passedVehicle?.id!);
  const { saveHandlers } = useDefaultCRUDHandlers(t("Job"));
  const { generatePartLabel } = useLabelGenerator();

  const [selectedPart, setSelectedPart] = useState<IPart | null>(null);

  const [vehicle, setVehicle] = useState<IVehicle | undefined>(passedVehicle);
  const [viewType, setViewType] = useState(0);
  const [availableParts, setAvailableParts] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!singleVehicle.isLoading && singleVehicle.data?.data) {
      setVehicle(singleVehicle.data?.data);
    }

    if (passedVehicle) {
      setVehicle(passedVehicle);
    }
  }, [singleVehicle.isLoading, singleVehicle.data, passedVehicle]);

  useEffect(() => {
    if (!jobs.isLoading && jobs.data && vehicle?.parts) {
      // Get all part category IDs that are currently in jobs
      const jobPartCategoryIds = new Set<string>();
      jobs.data.data.forEach(job => {
        job.jobPartCategories.forEach(jobPart => {
          if (jobPart.partCategoryId) {
            jobPartCategoryIds.add(jobPart.partCategoryId);
          }
        });
      });

      // Filter parts that are "On The Car" and not in any jobs
      const available = vehicle?.parts?.filter(part =>
        part &&
        part.partStatus === PartStatus["On The Car"] &&
        part.categoryId &&
        !jobPartCategoryIds.has(part.categoryId)
      );

      setAvailableParts(available);
    }
  }, [jobs.isLoading, jobs.data, vehicle?.parts]);

  // Parts for the active tab, minus the nulls the API can return in the array.
  const tabParts = useMemo(() => {
    const parts = viewType === 0 ? vehicle?.parts ?? [] : availableParts;
    return parts.filter((part) => part !== null && part !== undefined);
  }, [viewType, vehicle?.parts, availableParts]);

  const filteredParts = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return tabParts;

    return tabParts.filter((part: any) =>
      [part.title, part.tagNumber, part.partNumber]
        .some((field) => field?.toString().toLowerCase().includes(term))
    );
  }, [tabParts, search]);

  const pageCount = Math.max(Math.ceil(filteredParts.length / PAGE_SIZE), 1);
  const currentPage = Math.min(page, pageCount);
  const pagedParts = useMemo(
    () => filteredParts.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [filteredParts, currentPage]
  );

  //Back to the first page whenever the list underneath changes
  useEffect(() => {
    setPage(1);
  }, [viewType, search]);

  const handleCreateJob = (part: any) => {
    // Get the current job for this vehicle (if there is one)
    const currentJobId = jobs.data?.data && jobs.data.data.length > 0 ? jobs.data.data[0].id : null;

    const body = {
      partId: part.id,
      jobId: currentJobId
    }

    createWithPart.mutate(body, saveHandlers);
  };

  const handlePrint = async (part: IPart | null) => {
    if (!part) return;

    part.vehicle = vehicle ?? null;

    const html = await generatePartLabel(part);
    var iframe = document?.getElementById("frame") as HTMLIFrameElement;
    var pri = iframe.contentWindow;
    pri?.document.open();
    pri?.document.write(html);
    pri?.document.close();
    pri?.focus();

    //QR code doesn't show up without a timeout
    setTimeout(async () => {
      pri?.print();
    }, 1);
  };

  if (!vehicle) return <></>;

  const viewTypes = [
    { value: 0, label: t("All Parts") },
    { value: 1, label: t("Available") },
  ];

  const hasParts = !!vehicle.parts && vehicle.parts.length > 0;
  const showCreateJobButton = viewType === 1;

  const emptyMessage = () => {
    if (search.trim()) return t("No parts match your search.");
    if (viewType === 1) return t("No available parts found. All parts are either already in jobs or have been removed from the vehicle.");
    return t("No parts available for this vehicle.");
  };

  return (
    <Card
      title={t("Vehicle Parts")}
      bodyClassName="p-4 space-y-4"
      collapsible
      headerRight={
        hasParts ? (
          <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-600">
            {vehicle.parts!.length}
          </span>
        ) : undefined
      }
    >
      <VehiclePartHistory part={selectedPart} open={!!selectedPart} onClose={setSelectedPart} />

      {!hasParts ? (
        <p className="py-6 text-center text-sm text-gray-500">{t("No parts available for this vehicle.")}</p>
      ) : (
        <>
          <Tabs viewType={viewType} viewTypes={viewTypes} setViewType={setViewType} />

          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("Search by part, tag or part number...")}
              className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left">{t("Part")}</th>
                    <th className="px-3 py-2.5 text-left">{t("Tag Number")}</th>
                    <th className="px-3 py-2.5 text-right">{t("Price")}</th>
                    <th className="px-3 py-2.5 text-left">{t("Status")}</th>
                    <th className="px-4 py-2.5 text-right">
                      <span className="sr-only">{t("Actions")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white text-sm">
                  {pagedParts.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">
                        {emptyMessage()}
                      </td>
                    </tr>
                  ) : (
                    pagedParts.map((part: any) => (
                      <tr
                        key={part.id}
                        className="cursor-pointer transition-colors hover:bg-gray-50"
                        onClick={() => openInNewTab(`/stock/${part.id}`)}
                      >
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-x-3">
                            <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md bg-gray-50">
                              <PartImage part={part} width="max-w-[3.5rem] max-h-[3.5rem]" />
                            </div>
                            <div className="min-w-0">
                              <p className="truncate font-medium text-gray-900">{part.title}</p>
                              {part.partNumber && (
                                <p className="truncate text-xs text-gray-500">{part.partNumber}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-gray-700">
                          {part.tagNumber ?? <span className="text-gray-400">&mdash;</span>}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right text-gray-900">
                          {PriceFormat(part.price ?? part.enteredPrice ?? 0)}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-x-2">
                            <span
                              className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${partStatusColour(part.partStatus)}`}
                            >
                              {part.partStatus === null || part.partStatus === undefined
                                ? t("N/A")
                                : t(PartStatus[part.partStatus])}
                            </span>
                            {part.isCore && <RsCoreImage className="h-5 w-auto" />}
                          </div>
                        </td>
                        <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-x-1">
                            <IconButton label={t("Print Label")} onClick={() => handlePrint(part)}>
                              <PrinterIcon className="h-4 w-4" />
                            </IconButton>
                            <IconButton label={t("View History")} onClick={() => setSelectedPart(part)}>
                              <ClockIcon className="h-4 w-4" />
                            </IconButton>
                            {showCreateJobButton && (
                              <ButtonInput
                                isSubmit={false}
                                onClick={() => handleCreateJob(part)}
                                classes="!bg-primary-600 !text-white hover:!bg-primary-700 ml-1"
                                isLoading={createWithPart.isLoading}
                              >
                                <span className="flex items-center gap-x-1.5 px-2 py-1 text-white">
                                  <PlusIcon height={14} width={14} />
                                  {t("Create Job")}
                                </span>
                              </ButtonInput>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {filteredParts.length > PAGE_SIZE && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500">
                {t("Showing")} {(currentPage - 1) * PAGE_SIZE + 1}
                &ndash;{Math.min(currentPage * PAGE_SIZE, filteredParts.length)} {t("of")}{" "}
                {filteredParts.length}
              </p>
              <div className="flex items-center gap-x-2">
                <IconButton
                  label={t("Previous")}
                  onClick={() => setPage(Math.max(currentPage - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </IconButton>
                <span className="text-xs text-gray-600">
                  {currentPage} / {pageCount}
                </span>
                <IconButton
                  label={t("Next")}
                  onClick={() => setPage(Math.min(currentPage + 1, pageCount))}
                  disabled={currentPage >= pageCount}
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </IconButton>
              </div>
            </div>
          )}
        </>
      )}
      <iframe id="frame" title={t("Print frame")} className="absolute h-0 w-0"></iframe>
    </Card>
  )
}
